import { useCallback, useEffect, useMemo, useState } from 'react'
import { Warehouse } from '../../../../../../modules/API/Warehouse/warehouse'
import {
  IWarehouseLocation,
  IWarehouseLocationCreate,
  IWarehouseLocationUpdate,
} from '../../../../../../modules/API/Warehouse/models/locations'
import { ICoolingRoomsResponse } from '../../../../../../modules/API/Warehouse/models/rooms'
import { SelectOption } from '../../../../../../generic/universal-table/types/form.types'
import { handleNotifications } from '../../../../../../generic/utils/handleNotifications'
import { Defaults } from '../../../../../../modules/API/Defaults/defaults'
import { IWarhouseListItem } from '../../../../../../modules/API/Defaults/models/warhouseList'
import useDataFetcher from '../../../knisot/hooks/useFetchData'
import useTableFilters from '../../../../../../generic/universal-table/hooks/useTableFilters'

interface LocationListState {
  // null — not loaded yet; [] — loaded and empty.
  list: IWarehouseLocation[] | null
  isLoading: boolean
  error: string | null
}

export interface UseLocationControlReturn {
  rows: IWarehouseLocation[]
  totalCount: number
  isLoading: boolean
  error: string | null
  searchQuery: string
  handleSearch: (query: string) => void
  isUploadOpen: boolean
  toggleUploadModal: () => void
  handleUploadFile: (file: File, warehouseId: number) => Promise<IWarehouseLocation[]>
  resolveWarehouseLabel: (id: unknown) => string
  // null — not loaded yet; [] — loaded and empty.
  warehouseList: IWarhouseListItem[] | null
  resolveRoomLabel: (id: unknown) => string
  warehouseOptions: SelectOption[]
  roomOptions: SelectOption[]
  createLocation: (payload: IWarehouseLocationCreate) => Promise<void>
  updateLocation: (payload: IWarehouseLocationUpdate) => Promise<void>
  removeLocation: (recId: number) => Promise<void>
}

const LOAD_ERROR_MESSAGE = 'שגיאה בטעינת רשימת האיתורים'

const getServerMessage = (err: unknown): string | null => {
  if (typeof err === 'object' && err !== null) {
    const message = (err as { response?: { data?: { message?: unknown } } }).response?.data?.message
    if (typeof message === 'string' && message) return message
  }
  return null
}

const EMPTY_LOCATIONS: IWarehouseLocation[] = []

export const useLocationControl = (): UseLocationControlReturn => {
  const [state, setState] = useState<LocationListState>({
    list: null,
    isLoading: false,
    error: null,
  })
  const [isUploadOpen, setIsUploadOpen] = useState<boolean>(false)

  const { data: warehouseList } = useDataFetcher<IWarhouseListItem[]>(
    Defaults.getWarhouseList,
    'data',
  )

  const { data: roomsResponse } = useDataFetcher<ICoolingRoomsResponse>(
    Warehouse.getRoomList,
    'data',
  )

  const fetchLocations = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }))
    try {
      const response = await Warehouse.getLocationList()
      setState({ list: response.locations, isLoading: false, error: null })
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : LOAD_ERROR_MESSAGE
      setState((prev) => ({ ...prev, isLoading: false, error: message }))
    }
  }, [])

  useEffect(() => {
    fetchLocations()
  }, [fetchLocations])

  const { searchQuery, filteredRows, handleSearch } = useTableFilters<IWarehouseLocation>(
    state.list ?? EMPTY_LOCATIONS,
  )

  const toggleUploadModal = useCallback(() => {
    setIsUploadOpen((prev) => !prev)
  }, [])

  // The server answers the upload with the refreshed location list.
  const handleUploadFile = useCallback(async (file: File, warehouseId: number) => {
    const list = await Warehouse.uploadLocationsFile(file, warehouseId)
    setState({ list, isLoading: false, error: null })
    return list
  }, [])

  const warehouseNameById = useMemo(() => {
    const map = new Map<number, string>()
    warehouseList?.forEach((item) => map.set(item.WarehouseID, item.WarehouseDesc))
    return map
  }, [warehouseList])

  const resolveWarehouseLabel = useCallback(
    (id: unknown) => warehouseNameById.get(Number(id)) ?? String(id ?? ''),
    [warehouseNameById],
  )

  const createLocation = useCallback(async (payload: IWarehouseLocationCreate): Promise<void> => {
    try {
      const response = await Warehouse.createLocation(payload)
      setState((prev) => ({ ...prev, list: [...(prev.list ?? []), response.location] }))
      handleNotifications('האיתור נוסף בהצלחה', { type: 'success' })
    } catch (err) {
      handleNotifications(getServerMessage(err) ?? 'שגיאה בהוספת האיתור', { type: 'error' })
      throw err
    }
  }, [])

  const updateLocation = useCallback(async (payload: IWarehouseLocationUpdate): Promise<void> => {
    try {
      const response = await Warehouse.updateLocation(payload)
      setState((prev) => ({
        ...prev,
        list: (prev.list ?? []).map((row) =>
          row.RecID === response.location.RecID ? response.location : row,
        ),
      }))
      handleNotifications('האיתור עודכן בהצלחה', { type: 'success' })
    } catch (err) {
      handleNotifications(getServerMessage(err) ?? 'שגיאה בעדכון האיתור', { type: 'error' })
      throw err
    }
  }, [])

  // The error toast for a failed removal is shown by useTableEditing — no catch here.
  const removeLocation = useCallback(async (recId: number): Promise<void> => {
    await Warehouse.deleteLocation(recId)
    setState((prev) => ({
      ...prev,
      list: (prev.list ?? []).filter((row) => row.RecID !== recId),
    }))
    handleNotifications('האיתור נמחק בהצלחה', { type: 'success' })
  }, [])

  const warehouseOptions = useMemo<SelectOption[]>(
    () => warehouseList?.map((item) => ({ value: item.WarehouseID, label: item.WarehouseDesc })) ?? [],
    [warehouseList],
  )

  const roomOptions = useMemo<SelectOption[]>(
    () => roomsResponse?.rooms.map((room) => ({ value: room.RoomNum, label: room.RoomDesc })) ?? [],
    [roomsResponse],
  )

  const roomNameByNum = useMemo(() => {
    const map = new Map<number, string>()
    roomsResponse?.rooms.forEach((room) => map.set(room.RoomNum, room.RoomDesc))
    return map
  }, [roomsResponse])

  const resolveRoomLabel = useCallback(
    (id: unknown) => roomNameByNum.get(Number(id)) ?? String(id ?? ''),
    [roomNameByNum],
  )

  return {
    rows: filteredRows,
    totalCount: filteredRows.length,
    isLoading: state.isLoading,
    error: state.error,
    searchQuery,
    handleSearch,
    isUploadOpen,
    toggleUploadModal,
    handleUploadFile,
    resolveWarehouseLabel,
    warehouseList,
    resolveRoomLabel,
    warehouseOptions,
    roomOptions,
    createLocation,
    updateLocation,
    removeLocation,
  }
}
